import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Clock, User, RefreshCw, XCircle, Home } from 'lucide-react';
import { toast } from 'sonner';
import { ShiftNotes, shiftParticipantNames } from '@/components/shared/ShiftAddressNotes';
import { hasMyPickupRequest } from '@/components/calendar/PickupRequestsList';

export default function OpenShifts({ insideStaffPortal = false } = {}) {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [picking, setPicking] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // getcare-new-001: the API lists today onwards, soonest first; ask for the full page so none are cut off
      const res = await api.get('/roster/open-shifts', { params: { pageSize: 100 } });
      setShifts(res.data.items || res.data.data || []);
    } catch (e) {
      toast.error('Failed to load open shifts');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh on tab/window focus
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) fetchData(); };
    window.addEventListener('focus', fetchData);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', fetchData);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchData]);

  const pickupShift = async (id) => {
    setPicking(id);
    try {
      await api.post(`/roster/shifts/${id}/pickup-request`);
      toast.success('Shift request submitted!');
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail?.message || detail || 'Failed to pick up shift');
    } finally { setPicking(null); }
  };

  const withdrawPickup = async (id) => {
    setWithdrawing(id);
    try {
      await api.post(`/roster/shifts/${id}/pickup-withdraw`);
      toast.success('Shift request withdrawn');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to withdraw');
    } finally { setWithdrawing(null); }
  };

  const myPending = (shift) => hasMyPickupRequest(shift, user?.id);   // client#9: shifts hold a list of requests

  return (
    <div className="space-y-6 animate-fade-in">
      <div className={`flex items-center ${insideStaffPortal ? 'justify-end' : 'justify-between'}`}>
        {!insideStaffPortal && (
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Open Shifts</h1>
            <p className="text-muted-foreground mt-1">Available shifts in your area</p>
          </div>
        )}
        <Button variant="outline" size="sm" onClick={() => { setRefreshing(true); fetchData(); }} disabled={refreshing} data-testid="open-shifts-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {shifts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No open shifts available</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {shifts.map((shift) => {
            const twoToOne = (shift.support_ratio || '').trim() === '2:1';
            const mine = myPending(shift);
            return (
              <Card key={shift.id} className="hover:border-primary/30 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      {/* client#15: every participant (SIL residents too) + the SIL house */}
                      <p className="font-medium" data-testid={`open-shift-participants-${shift.id}`}>{shiftParticipantNames(shift) || 'Participant'}</p>
                      {shift.house_name && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid={`open-shift-house-${shift.id}`}>
                          <Home className="h-3.5 w-3.5" /> {shift.house_name}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {mine && <Badge variant="secondary" data-testid={`open-shift-mine-${shift.id}`}>Request pending</Badge>}
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="font-mono tabular-nums">{shift.date} | {shift.start_time}-{shift.end_time}</span>
                    </div>
                    {shift.state_id && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{shift.state_id} - {shift.branch_id}</span>
                      </div>
                    )}
                    {/* client#8: shift notes visible before requesting the shift */}
                    <div data-testid={`open-shift-notes-${shift.id}`}><ShiftNotes notes={shift.notes} compact /></div>
                  </div>
                  {mine ? (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => withdrawPickup(shift.id)}
                      disabled={withdrawing === shift.id}
                      data-testid={`shift-card-open-withdraw-button-${shift.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      {withdrawing === shift.id ? 'Withdrawing…' : 'Withdraw request'}
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      variant="secondary"
                      onClick={() => pickupShift(shift.id)}
                      disabled={picking === shift.id || twoToOne}
                      data-testid={`shift-card-open-pickup-button-${shift.id}`}
                    >
                      <User className="h-4 w-4 mr-2" />
                      {twoToOne
                        ? '2:1 — admin-assigned only'
                        : (picking === shift.id ? 'Requesting...' : 'Request Shift')}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
